import React, { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { Link, useNavigate } from 'react-router-dom';
import './MyDashboard.css';
import moment from 'moment';
import OnboardingModal from './OnboardingModal';
import NotificationBell from './NotificationBell';
import { jwtDecode } from 'jwt-decode';

const Dashboard = () => {
  const [userData, setUserData] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [popupEvent, setPopupEvent] = useState(null);
  const navigate = useNavigate();
  const [showSessionModal, setShowSessionModal] = useState(false);
  const token = localStorage.getItem('token');
  const [remainingTime, setRemainingTime] = useState(null);
  
  // NEW: Pending AI conversations count
  const [pendingAICount, setPendingAICount] = useState(0);
  const [profilePhotoUrl, setProfilePhotoUrl] = useState(null);

  // Subscription status
  const [subscription, setSubscription] = useState(null);
  const [circle, setCircle] = useState(null);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const EVENTS_PREVIEW = 5;
  const [showOnboarding, setShowOnboarding] = useState(false);

  const currentYear = new Date().getFullYear();

  const handleLogout = useCallback(async () => {
    // Preserve onboarding flags before clearing — keyed by userId
    const onboardingKeys = Object.keys(localStorage).filter(k => k.startsWith('hasSeenOnboarding_'));
    const preserved = {};
    onboardingKeys.forEach(k => { preserved[k] = localStorage.getItem(k); });

    localStorage.clear();

    // Restore onboarding flags
    Object.entries(preserved).forEach(([k, v]) => localStorage.setItem(k, v));

    if (Capacitor.isNativePlatform()) {
      await Preferences.clear();
    }
    navigate('/login');
  }, [navigate]);

  const checkTokenExpiration = useCallback(() => {
    if (token) {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;

      if (decodedToken.exp < currentTime) {
        handleLogout();
      } else {
        setRemainingTime(decodedToken.exp - currentTime);
      }
    } else {
      handleLogout();
    }
  }, [token, handleLogout]);

  useEffect(() => {
    checkTokenExpiration();
    const intervalId = setInterval(() => {
      checkTokenExpiration();
    }, 60000);
    return () => clearInterval(intervalId);
  }, [checkTokenExpiration]);

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/user/dashboard`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    })
      .then((response) => response.json())
      .then((data) => {
        setUserData(data.user);

        // Onboarding — keyed by user _id from API (not localStorage which may be stale)
        const uid = data.user?._id;
        if (uid) {
          const key = `hasSeenOnboarding_${uid}`;
          if (!localStorage.getItem(key)) {
            setShowOnboarding(true);
          }
        }

        console.log('Fetched upcoming events:', data.upcomingEvents);

        if (Array.isArray(data.upcomingEvents)) {
          const filteredAndSortedEvents = data.upcomingEvents
            .filter((event) => {
              const eventDate = moment(event.startTime);
              const today = moment().startOf('day');
              const sevenDaysFromNow = moment().add(7, 'days').endOf('day');
              return eventDate.isBetween(today, sevenDaysFromNow, null, '[]');
            })
            .sort((a, b) => moment(a.startTime).valueOf() - moment(b.startTime).valueOf());

          setUpcomingEvents(filteredAndSortedEvents);
        } else {
          setUpcomingEvents([]);
        }
      })
      .catch((error) => {
        console.error('Error fetching dashboard data:', error);
      });
  }, []);

  // Fetch profile photo
  useEffect(() => {
    const fetchPhoto = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/users/profile-photo`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (response.ok) {
          const data = await response.json();
          if (data.profilePhotoUrl) setProfilePhotoUrl(data.profilePhotoUrl);
        }
      } catch (error) {
        console.error('Error fetching profile photo:', error);
      }
    };
    fetchPhoto();
  }, []);

  // NEW: Fetch pending AI conversations count
  useEffect(() => {
    const fetchPendingAI = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/persona/conversations/pending`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setPendingAICount(data.conversations?.length || 0);
        }
      } catch (error) {
        console.error('Error fetching pending AI conversations:', error);
      }
    };
    fetchPendingAI();
  }, []);

  // Fetch circle membership
  useEffect(() => {
    const fetchCircle = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/circles/mine`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (response.ok) {
          const data = await response.json();
          setCircle(data.circle);
        }
      } catch (error) {
        // No circle — that's fine, don't log
      }
    };
    fetchCircle();
  }, []);
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/billing/status`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setSubscription(data);
        }
      } catch (error) {
        console.error('Error fetching subscription:', error);
      }
    };
    fetchSubscription();
  }, []);

  useEffect(() => {
    const userId = localStorage.getItem('userId');
    if (!userId) {
      console.error('UserId not found in localStorage');
    }
  }, []);

  const checkForUpcomingEvents = useCallback(() => {
    const currentTime = new Date();
    const eventThreshold = 10 * 60 * 1000; // 10 minutes

    upcomingEvents.forEach((event) => {
      const eventStartTime = new Date(event.startTime);
      const timeDifference = eventStartTime - currentTime;

      if (timeDifference > 0 && timeDifference <= eventThreshold) {
        setPopupEvent(event);
      }
    });
  }, [upcomingEvents]);

  useEffect(() => {
    const intervalId = setInterval(checkForUpcomingEvents, 10 * 60 * 1000);
    return () => clearInterval(intervalId);
  }, [checkForUpcomingEvents]);

  const closePopup = () => {
    setPopupEvent(null);
  };

  const refreshSession = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/auth/refresh-token`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.token) {
        localStorage.setItem('token', data.token);
        setShowSessionModal(false);
        alert('Session refreshed successfully!');
      }
    } catch (error) {
      console.error('Failed to refresh session:', error);
      handleLogout();
    }
  };

  const formatGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-background">
        <div className="wave-pattern"></div>
      </div>

      <div className="dashboard-container">
        {/* Header Section */}
        <header className="dashboard-header">
          <div className="dashboard-logo">
            <div className="wave-symbol">
              <div className="wave-element"></div>
              <div className="wave-element-small"></div>
            </div>
            <div className="logo-text">
              <span className="logo-air">Air</span><span className="logo-flyp">Flyp</span>
            </div>
          </div>
          
          <div className="welcome-section">
            {profilePhotoUrl ? (
              <img src={profilePhotoUrl} alt="Profile" className="dash-profile-photo" />
            ) : (
              <div className="dash-profile-initials">
                {(userData?.firstname?.[0] || '').toUpperCase()}{(userData?.lastname?.[0] || '').toUpperCase()}
              </div>
            )}
            <div className="welcome-text">
              <h1>
                {formatGreeting()}, {userData?.firstname || userData?.username}!
                {['pro','circle_member','circle_3','circle_5','circle_10'].includes(subscription?.plan?.toLowerCase()) && (
                  <span className="pro-plan-badge">
                    {subscription?.plan?.toLowerCase() === 'circle_10' ? 'CIRCLE 10'
                      : subscription?.plan?.toLowerCase() === 'circle_5' ? 'CIRCLE 5'
                      : subscription?.plan?.toLowerCase() === 'circle_3' ? 'CIRCLE 3'
                      : subscription?.plan?.toLowerCase() === 'circle_member' ? 'CIRCLE'
                      : 'PRO'}
                  </span>
                )}
              </h1>
              {userData?.fullName && <h2 className="user-fullname">{userData.fullName}</h2>}
              <p className="platform-tagline">
                {['pro','circle_member','circle_3','circle_5','circle_10'].includes(subscription?.plan?.toLowerCase())
                  ? subscription?.plan?.toLowerCase().startsWith('circle')
                    ? `Your Circle workspace — all features unlocked`
                    : 'Your Pro workspace — all features unlocked'
                  : 'Your workspace — get started below'}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            {circle && <NotificationBell circleId={circle._id} />}
            <button onClick={handleLogout} className="logout-button" title="Sign Out">
              <span className="logout-icon">↪</span>
              <span>Sign Out</span>
            </button>
          </div>
        </header>

        {/* Quick Actions */}
        <section className="quick-actions">
          <h2>Quick Actions</h2>
          <div className="action-grid">
            {/* AI Persona — hero card */}
            <Link to="/persona" className="action-card primary">
              <div className="action-icon" style={{fontSize:'1.8rem', fontStyle:'normal'}}>✦</div>
              <div className="action-content">
                <h3>AI Persona</h3>
                <p>Your AI assistant for clients</p>
              </div>
              {pendingAICount > 0 && (
                <div className="action-badge">{pendingAICount}</div>
              )}
            </Link>

            {/* Calendar */}
            <Link to="/calendar" className="action-card accent">
              <div className="action-icon">🗓</div>
              <div className="action-content">
                <h3>View Calendar</h3>
                <p>Manage your schedule</p>
              </div>
            </Link>

            {/* Clients */}
            <Link to="/clients" className="action-card accent">
              <div className="action-icon">👥</div>
              <div className="action-content">
                <h3>Client Management</h3>
                <p>View and organize clients</p>
              </div>
            </Link>
          </div>
        </section>

        {/* Upgrade Banner - show only for free users */}
        {/* ── Circle Snapshot ── */}
        {['pro','circle_member','circle_3','circle_5','circle_10'].includes(subscription?.plan?.toLowerCase()) && circle && (
          <section className="circle-snapshot">
            <div className="circle-snapshot-header">
              <div className="circle-snapshot-title">
                <span className="circle-snapshot-mark">✦</span>
                <div>
                  <span className="circle-snapshot-label">Circles</span>
                  <h3>{circle.name}</h3>
                  <p>{circle.members?.length}/{circle.maxMembers} members
                    {circle.pendingInvites?.filter(i => new Date(i.expiresAt) > new Date()).length > 0 && (
                      <span className="circle-snapshot-pending">
                        {' '}· {circle.pendingInvites.filter(i => new Date(i.expiresAt) > new Date()).length} pending
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <Link to={`/circles/${circle._id}/manage`} className="circle-snapshot-manage">
                Manage →
              </Link>
            </div>

            {/* Member avatars */}
            <div className="circle-snapshot-members">
              {circle.members?.slice(0, 5).map((member, i) => {
                const u = member.userId;
                const initials = u?.firstname
                  ? `${u.firstname[0]}${u.lastname?.[0] || ''}`.toUpperCase()
                  : '?';
                const colors = ['#1B2B4B','#E8654A','#10B981','#3B82F6','#8B5CF6'];
                return (
                  <div
                    key={member._id || i}
                    className="circle-snapshot-avatar"
                    style={{ background: colors[i % colors.length], marginLeft: i > 0 ? '-8px' : '0', zIndex: 10 - i }}
                    title={u?.firstname ? `${u.firstname} ${u.lastname}` : 'Member'}
                  >
                    {initials}
                  </div>
                );
              })}
              {circle.members?.length > 5 && (
                <div className="circle-snapshot-avatar circle-snapshot-more">
                  +{circle.members.length - 5}
                </div>
              )}
              <span className="circle-snapshot-seats">
                {circle.maxMembers - circle.members?.length} seat{circle.maxMembers - circle.members?.length !== 1 ? 's' : ''} remaining
              </span>
            </div>

            {/* Quick actions */}
            <div className="circle-snapshot-actions">
              <Link to={`/circles/${circle._id}/clients`} className="circle-snapshot-btn">
                👥 Shared clients
              </Link>
              <Link to={`/circles/${circle._id}/manage`} className="circle-snapshot-btn">
                ✉ Invite member
              </Link>
            </div>
          </section>
        )}

        {/* ── Upgrade section — free users see both options ── */}
        {subscription?.plan?.toLowerCase() === 'free' && !circle && (
          <section className="upgrade-section">
            <p className="upgrade-section-label">Unlock more</p>
            <div className="upgrade-cards">

              {/* Pro card */}
              <div className="upgrade-card">
                <div className="upgrade-card-header">
                  <span className="upgrade-card-icon">💎</span>
                  <div>
                    <h3>Pro</h3>
                    <span className="upgrade-card-price">$29<span>/mo</span></span>
                  </div>
                </div>
                <ul className="upgrade-card-features">
                  <li>✓ Unlimited clients</li>
                  <li>✓ AI Persona</li>
                  <li>✓ Video conferencing</li>
                  <li>✓ Google Calendar sync</li>
                  <li>✓ Priority support</li>
                </ul>
                <Link to="/pricing" className="upgrade-card-btn secondary">
                  Upgrade to Pro
                </Link>
              </div>

              {/* Circle card */}
              <div className="upgrade-card featured">
                <div className="upgrade-card-badge">Includes Pro</div>
                <div className="upgrade-card-header">
                  <span className="upgrade-card-icon">✦</span>
                  <div>
                    <h3>Circles</h3>
                    <span className="upgrade-card-price">$79<span>/mo</span></span>
                  </div>
                </div>
                <ul className="upgrade-card-features">
                  <li>✓ Everything in Pro</li>
                  <li>✓ Shared client workspace</li>
                  <li>✓ Team notes & summaries</li>
                  <li>✓ Circle notifications</li>
                  <li>✓ Up to 10 members</li>
                </ul>
                <Link to="/circles/create" className="upgrade-card-btn primary">
                  Create a Circle
                </Link>
              </div>

            </div>
          </section>
        )}

        {/* Circle upsell — Pro users without a circle */}
        {['pro','circle_member','circle_3','circle_5','circle_10'].includes(subscription?.plan?.toLowerCase()) && !circle && (
          <section className="circle-upsell">
            <div className="circle-upsell-mark">✦</div>
            <div className="circle-upsell-text">
              <h3>Ready to collaborate?</h3>
              <p>Create a Circle and share clients, notes, and AI summaries with your team. Includes all Pro features — from $79/mo.</p>
            </div>
            <Link to="/circles/create" className="circle-upsell-btn">
              Create a Circle →
            </Link>
          </section>
        )}

        {/* Upcoming Events */}
        <section className="upcoming-events-section">
          <div className="section-header">
            <h2>Your Upcoming Week</h2>
            <p>Next 7 days of scheduled sessions</p>
            <Link to="/calendar" style={{fontSize:'0.9rem', fontWeight:600, color:'var(--accent)', textDecoration:'none', marginTop:'0.5rem', display:'inline-block'}}>View full calendar →</Link>
          </div>
          
          {upcomingEvents.length ? (
            <div className="events-container">
              {(showAllEvents ? upcomingEvents : upcomingEvents.slice(0, EVENTS_PREVIEW)).map((evt) => {
                const eventDate = moment.utc(evt.startTime).local().format('MMM DD');
                const eventStartTime = moment.utc(evt.startTime).local().format('h:mm A');
                const dayOfWeek = moment.utc(evt.startTime).local().format('dddd');
                
                const clientDisplayName = evt.clientName || (evt.clientId ? `${evt.clientId.firstName} ${evt.clientId.lastName}` : 'Client Unknown');
                const formattedTitle = evt.title.includes('Client Booking Request') ? clientDisplayName : evt.title;

                return (
                  <div key={evt._id} className="event-card">
                    <div className="event-time">
                      <div className="event-date">{eventDate}</div>
                      <div className="event-day">{dayOfWeek}</div>
                      <div className="event-clock">{eventStartTime}</div>
                    </div>
                    
                    <div className="event-details">
                      {evt.clientId ? (
                        <Link to={`/clientdashboard/${evt.clientId}`} className="event-link">
                          <div className="event-title">{formattedTitle}</div>
                          <div className="event-meta">
                            <span className="client-badge">Client Session</span>
                          </div>
                        </Link>
                      ) : (
                        <div className="event-content">
                          <div className="event-title">{formattedTitle}</div>
                          <div className="event-meta">
                            <span className="general-badge">Appointment</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {upcomingEvents.length > EVENTS_PREVIEW && (
                <button
                  className="events-toggle-btn"
                  onClick={() => setShowAllEvents(!showAllEvents)}
                >
                  {showAllEvents
                    ? 'Show less ▴'
                    : `+ ${upcomingEvents.length - EVENTS_PREVIEW} more this week ▾`}
                </button>
              )}
            </div>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">📭</div>
              <h3>Your week is open</h3>
              <p>No appointments scheduled for the next 7 days</p>
              <Link to="/calendar" className="empty-action">View Full Calendar</Link>
            </div>
          )}
        </section>

        {/* Management Actions */}
        <section className="management-section">
          <h2>Manage Your Workspace</h2>
          <div className="management-grid">

            {/* AI Persona */}
            <Link to="/persona" className="management-card ai-highlight">
              <div className="management-icon" style={{fontSize:'1.4rem'}}>✦</div>
              <div className="management-content">
                <h3>AI Persona</h3>
                <p>Configure your AI assistant</p>
                {pendingAICount > 0 && (
                  <span className="pending-badge">{pendingAICount} pending review</span>
                )}
              </div>
              <div className="management-arrow">→</div>
            </Link>

            {/* Circles — links to manage if exists, create if not */}
            <Link
              to={circle ? `/circles/${circle._id}/manage` : '/circles/create'}
              className="management-card bar-purple"
            >
              <div className="management-icon">✦✦</div>
              <div className="management-content">
                <h3>Circles</h3>
                <p>{circle ? circle.name : 'Shared workspaces for your team'}</p>
                {circle ? (
                  <span className="pro-badge-small">{circle.members?.length}/{circle.maxMembers} members</span>
                ) : (
                  <span className="upgrade-badge-small">New</span>
                )}
              </div>
              <div className="management-arrow">→</div>
            </Link>

            {/* Integrations */}
            <Link to="/settings#integrations" className="management-card bar-blue">
              <div className="management-icon">🔗</div>
              <div className="management-content">
                <h3>Integrations</h3>
                <p>Google Calendar, booking link, calendar sync</p>
              </div>
              <div className="management-arrow">→</div>
            </Link>

            {/* AI Settings */}
            <Link to="/settings#ai-settings" className="management-card bar-accent">
              <div className="management-icon">🎛</div>
              <div className="management-content">
                <h3>AI settings</h3>
                <p>Tone, writing style, custom criteria</p>
              </div>
              <div className="management-arrow">→</div>
            </Link>

            {/* Video Conference — hidden, phasing out
            <Link to="/videoconference" className="management-card bar-blue">
              <div className="management-icon">🎥</div>
              <div className="management-content">
                <h3>Video conference</h3>
                <p>Start or join a video session</p>
              </div>
              <div className="management-arrow">→</div>
            </Link>
            */}

            {/* Pricing / Subscription */}
            <Link to="/pricing" className="management-card pricing-highlight">
              <div className="management-icon">💎</div>
              <div className="management-content">
                <h3>{['pro','circle_member','circle_3','circle_5','circle_10'].includes(subscription?.plan?.toLowerCase()) ? 'Manage plan' : 'Upgrade to Pro'}</h3>
                <p>{['pro','circle_member','circle_3','circle_5','circle_10'].includes(subscription?.plan?.toLowerCase()) ? 'Manage your subscription' : 'Unlock unlimited features'}</p>
                {subscription?.plan === 'free' && <span className="upgrade-badge-small">Free plan</span>}
                {['pro','circle_member','circle_3','circle_5','circle_10'].includes(subscription?.plan?.toLowerCase()) && (
                  <span className="pro-badge-small">
                    {subscription?.plan?.toLowerCase() === 'circle_10' ? 'Circle 10'
                      : subscription?.plan?.toLowerCase() === 'circle_5' ? 'Circle 5'
                      : subscription?.plan?.toLowerCase() === 'circle_3' ? 'Circle 3'
                      : subscription?.plan?.toLowerCase() === 'circle_member' ? 'Circle member'
                      : 'Pro plan'}
                  </span>
                )}
              </div>
              <div className="management-arrow">→</div>
            </Link>

            {/* Add Client */}
            <Link to="/createclient" className="management-card bar-accent">
              <div className="management-icon">👤</div>
              <div className="management-content">
                <h3>Add new client</h3>
                <p>Expand your client list</p>
              </div>
              <div className="management-arrow">→</div>
            </Link>

            {/* Settings */}
            <Link to="/settings" className="management-card">
              <div className="management-icon">⚙️</div>
              <div className="management-content">
                <h3>Settings</h3>
                <p>Account, work hours, preferences</p>
              </div>
              <div className="management-arrow">→</div>
            </Link>

          </div>
        </section>

        {/* Footer */}
        <footer className="dashboard-footer">
          <p>&copy; {currentYear} AirFlyp — built for modern teams.</p>
          <p style={{ marginTop: '0.5rem' }}>
            <Link to="/help" style={{ fontSize: '0.85rem', color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }}>
              Help & Guide
            </Link>
            <span style={{ color: 'var(--text-light)', margin: '0 0.5rem' }}>·</span>
            <a href="mailto:support@airflyp.com" style={{ fontSize: '0.85rem', color: 'var(--text-light)', textDecoration: 'none' }}>
              Contact support
            </a>
          </p>
          <p style={{ fontSize: '0.75rem', opacity: 0.5, marginTop: '0.25rem' }}>v1.3.5</p>
        </footer>
      </div>

      {/* Onboarding modal — first login only */}
      {showOnboarding && (
        <OnboardingModal onComplete={() => {
          const uid = userData?._id;
          if (uid) localStorage.setItem(`hasSeenOnboarding_${uid}`, 'true');
          setShowOnboarding(false);
        }} />
      )}

      {/* Session Expiration Modal */}
      {showSessionModal && (
        <div className="modal-overlay">
          <div className="session-modal">
            <div className="modal-header">
              <h2>Session Expiring Soon</h2>
            </div>
            <div className="modal-body">
              <p>Your session will expire in less than 2 minutes. Would you like to extend your session?</p>
            </div>
            <div className="modal-actions">
              <button onClick={refreshSession} className="modal-btn primary">
                Extend Session
              </button>
              <button onClick={handleLogout} className="modal-btn secondary">
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Event Popup */}
      {popupEvent && (
        <div className="notification-overlay">
          <div className="event-notification">
            <div className="notification-header">
              <h3>Session Starting Soon</h3>
              <button onClick={closePopup} className="close-notification">×</button>
            </div>
            <div className="notification-body">
              <div className="notification-title">{popupEvent.title}</div>
              <div className="notification-time">
                Starts at {moment.utc(popupEvent.startTime).local().format('h:mm A')}
              </div>
              {popupEvent.location && (
                <div className="notification-location">Location: {popupEvent.location}</div>
              )}
            </div>
            <div className="notification-actions">
              <button onClick={closePopup} className="notification-btn">
                Acknowledge
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;